import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMondayImages } from '../api/PublicIframe.api';
import '../styles/PublicIframe.css';
import '../styles/PublicMondayIframe.css';

const AUTOPLAY_INTERVAL_MS = 3000;

const PublicMondayIframe = ({ feedId, label = 'Lunes', refreshInterval = 300000 }) => {
  const [images, setImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const containerRef = useRef(null);
  // Ensures the "default to most recent Monday" behavior only happens once, on first load, so a
  // background refetch never yanks the viewer's manual slider position back to the end.
  const hasInitializedIndexRef = useRef(false);

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMondayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const fetchImages = useCallback(async () => {
    try {
      const data = await getMondayImages(feedId);
      const mondayImages = Array.isArray(data) ? data : [];
      setImages(mondayImages);

      if (mondayImages.length === 0) {
        setErrorMessage('Sin imágenes de lunes disponibles');
        setError(true);
      } else {
        setError(false);
        setErrorMessage('');
        if (!hasInitializedIndexRef.current) {
          setSelectedIndex(mondayImages.length - 1);
          hasInitializedIndexRef.current = true;
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching monday images:', err);
      setErrorMessage('Sin señal');
      setError(true);
      setLoading(false);
    }
  }, [feedId]);

  useEffect(() => {
    if (!feedId) {
      setError(true);
      setErrorMessage('Falta el parámetro feedId');
      return;
    }

    fetchImages();
    const interval = setInterval(fetchImages, refreshInterval);
    return () => clearInterval(interval);
  }, [feedId, refreshInterval, fetchImages]);

  const handleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const handlePrev = () => {
    setSelectedIndex((current) => (current - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setSelectedIndex((current) => (current + 1) % images.length);
  };

  const togglePlaying = () => {
    setIsPlaying((current) => !current);
  };

  const selected = images[selectedIndex];
  const ready = !loading && !error && images.length > 0 && selected;

  // Auto-advance in a loop (wraps back to the first Monday after the last). Re-scheduled on every
  // index change (manual or automatic) so a manual click always grants a fresh full interval before
  // the next auto-advance, instead of firing right after.
  useEffect(() => {
    if (!isPlaying || !ready || images.length <= 1) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setSelectedIndex((current) => (current + 1) % images.length);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearTimeout(timeoutId);
  }, [isPlaying, ready, images.length, selectedIndex]);

  return (
    <div className="lsl lsm" id="lsm" ref={containerRef}>
      {/* Every Monday's <img> stays mounted (hidden via opacity) instead of swapping a single
          element's src, so each image is downloaded once by the browser and reused for the rest
          of the loop instead of being re-fetched from S3 on every step. */}
      {ready && images.map((img, idx) => (
        <img
          key={img.url}
          className={`lsl-img lsm-frame ${idx === selectedIndex ? 'lsm-frame-active' : ''}`}
          alt="Imagen de lunes"
          src={img.url}
          style={{ objectFit: 'cover' }}
        />
      ))}

      <div className="lsl-logo">
        <span className="lsl-mark"></span>
        {feedId !== 'liwkb09qt3j6' && <span>LISUAL</span>}
      </div>

      <div className="lsl-ctrl">
        {ready && images.length > 1 && (
          <button
            className="lsl-btn"
            title={isPlaying ? 'Pausar' : 'Reproducir'}
            onClick={togglePlaying}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="5" width="4" height="14"></rect>
                <rect x="14" y="5" width="4" height="14"></rect>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="6 4 20 12 6 20 6 4"></polygon>
              </svg>
            )}
          </button>
        )}
        <button
          className="lsl-btn"
          title="Pantalla completa"
          onClick={handleFullscreen}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4"></path>
          </svg>
        </button>
      </div>

      {ready && images.length > 1 && (
        <>
          <button
            className="lsm-nav lsm-nav-prev"
            title="Lunes anterior"
            onClick={handlePrev}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="lsm-nav lsm-nav-next"
            title="Lunes siguiente"
            onClick={handleNext}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <div className="lsm-carousel-info">
            <span className="lsm-carousel-date">{formatMondayDate(selected.date)}</span>
            <span className="lsm-carousel-count">{selectedIndex + 1} / {images.length}</span>
          </div>
        </>
      )}

      <div className="lsl-meta">
        <span className="lsl-label">
          {label}
        </span>
        <span className="lsl-stat">
          <span className="lsl-time">
            {ready ? `${formatMondayDate(selected.date)} ${formatTime(selected.date)}` : '--:--'}
          </span>
        </span>
      </div>

      {loading && !error && (
        <div className="lsl-cover">
          <div className="lsl-spin"></div>
          <small>Cargando</small>
        </div>
      )}

      {error && (
        <div className="lsl-cover">
          <span className="ico">⚠</span>
          <small>{errorMessage || 'Sin señal'}</small>
        </div>
      )}
    </div>
  );
};

export default PublicMondayIframe;
